import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Chart from 'chart.js/auto';
import { Radar } from 'react-chartjs-2';
import bgImage from '../images/background.png';
import { PROFILE_IMAGES } from '../constants/profileImages';
import wineA from '../images/wine-a.png';
import wineB from '../images/wine-b.png';
import wineC from '../images/wine-c.png';
import wineD from '../images/wine-d.png';

function StIcon({ name, size = 20 }) {
    const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (name) {
        case 'back': return <svg {...props}><path d="M15 18l-6-6 6-6" /></svg>;
        case 'eye': return (
            <svg {...props}>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
            </svg>
        );
        case 'aroma': return (
            <svg {...props}>
                <path d="M11 4 C11 4 9 10 9 14 C9 17 7 18 7 20"/>
                <path d="M13 4 C13 4 15 10 15 14 C15 17 17 18 17 20"/>
                <path d="M7 20 Q12 23 17 20"/>
            </svg>
        );
        case 'drop': return (
            <svg width={size} height={size} viewBox="0 0 24 24">
                <path
                    d="M7 6 L17 6 Q18 6 18 13 Q18 21 12 22 Q6 21 6 13 Q6 6 7 6 Z"
                    fill="currentColor"
                    fillOpacity="0.22"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
                <line x1="12" y1="6" x2="12" y2="20" stroke="currentColor" strokeWidth="1.3" strokeOpacity="0.7"/>
            </svg>
        );
        case 'grape': return (
            <svg {...props} strokeWidth={1.3}>
                <path d="M12 6.2V3" strokeWidth={1.5}/>
                <path d="M12 3 Q15.5 1 18 3"/>
                <circle cx="8" cy="8.5" r="2.3"/>
                <circle cx="12" cy="8.5" r="2.3"/>
                <circle cx="16" cy="8.5" r="2.3"/>
                <circle cx="10" cy="12.5" r="2.3"/>
                <circle cx="14" cy="12.5" r="2.3"/>
                <circle cx="12" cy="16.5" r="2.3"/>
            </svg>
        );
        case 'globe': return (
            <svg {...props}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
        );
        case 'pin': return (
            <svg {...props}>
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
            </svg>
        );
        case 'tag': return (
            <svg {...props}>
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="2.5"/>
            </svg>
        );
        default: return null;
    }
}

function StDropdown({ options, value, onChange, accentColor }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const current = options.find(o => String(o.value) === String(value)) || options[0];

    return (
        <div className="st-dropdown" ref={ref}>
            <button
                className={`st-dropdown-trigger${open ? ' st-open' : ''}`}
                onClick={() => setOpen(p => !p)}
                type="button"
            >
                <span className="st-dot" style={{ background: accentColor }} />
                <span className="st-dropdown-label">{current?.label || '—'}</span>
                <svg
                    className="st-dropdown-chevron"
                    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    width="10" height="6" viewBox="0 0 10 6"
                    fill="none"
                >
                    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && (
                <div className="st-dropdown-menu">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            className={`st-dropdown-option${String(opt.value) === String(value) ? ' st-selected' : ''}`}
                            onClick={() => { onChange(opt); setOpen(false); }}
                            type="button"
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Stats() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);
    const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'dark');

    useEffect(() => {
        const obs = new MutationObserver(() => {
            setTheme(document.documentElement.getAttribute('data-theme') || 'dark');
        });
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => obs.disconnect();
    }, []);
    const [profileInfo, setProfileInfo] = useState({ username: '', average: 0.00, wines: 0, profile: 0, id: 0 });
    const [userData, setUserData] = useState({ profile: 0 });
    const [fetchError, setFetchError] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const [myUserData, setMyUserData] = useState({ profile: 0 });
    const [selectedButton, setSelectedButton] = useState('monthly');
    const [personalData, setPersonalData] = useState([0, 0, 0, 0, 0, 0, 0]);
    const [generalData, setGeneralData] = useState([0, 0, 0, 0, 0, 0, 0]);
    const [personalLabel, setPersonalLabel] = useState(t('common.personal'));
    const [generalLabel, setGeneralLabel] = useState(t('stats.participants_level'));
    const [firstValue, setFirstValue] = useState('user');
    const [secondValue, setSecondValue] = useState('general');
    const swipeStartX = useRef(null);
    const { id } = useParams();
    const [myWinesOrder, setMyWinesOrder] = useState({});
    const [generalWinesOrder, setGeneralWinesOrder] = useState([]);

    const getWineImage = (wine) => {
        switch (wine) {
            case 'A': return wineA;
            case 'B': return wineB;
            case 'C': return wineC;
            case 'D': return wineD;
            default: return null;
        }
    };

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
        fetchUserData(id);
        const storedUserInfo = localStorage.getItem('userLogged');
        if (storedUserInfo) {
            const userInfo = JSON.parse(storedUserInfo);
            setProfileInfo({ username: userInfo.username, average: userInfo.average || 0.00, wines: userInfo.wines || 0, id: userInfo.id || 0, profile: userInfo.profile || 0 });
            setPersonalLabel(userInfo.username);
            fetchMyUserData(userInfo.id);
        }
    }, [retryKey]);

    useEffect(() => {
        if (profileInfo.id !== 0 && userData.id !== 0) {
            fetchPersonalData(profileInfo.id);
            if (profileInfo.id == id) {
                fetchGeneralData();
            } else {
                fetchSecondUserData(userData.id);
                setGeneralLabel(userData.username);
            }
            fetchWinesOrderData(userData.id);
        }
    }, [userData.id, profileInfo.id]);

    useEffect(() => {
        if (!selectedButton) return;
        if (firstValue === 'user') {
            fetchPersonalData(profileInfo.id);
        } else {
            fetchFirstWinegangData(firstValue);
        }
        if (secondValue === 'general') {
            fetchGeneralData();
        } else if (secondValue === 'user') {
            fetchSecondUserData(userData.id);
        } else {
            fetchSecondWinegangData(secondValue);
        }
    }, [selectedButton]);

    const fetchUserData = (userId) => {
        setFetchError(false);
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/user/${userId}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) { setUserData(JSON.parse(this.responseText)); }
                else { setFetchError(true); }
            }
        };
        xhr.send();
    };

    const fetchMyUserData = (userId) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/user/${userId}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) { setMyUserData(JSON.parse(this.responseText)); }
        };
        xhr.send();
    };

    const fetchPersonalData = (userId) => {
        const period = selectedButton === 'monthly' ? 'last_month' : 'all_time';
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/userStats/${userId}?period=${period}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const s = JSON.parse(this.responseText);
                setPersonalData([s.visual_stats * 100, s.olfactory_stats * 100, s.taste_stats * 100, s.variety_stats * 100, s.country_stats * 100, s.region_stats * 100, s.price_stats * 100]);
            }
        };
        xhr.send();
    };

    const fetchSecondUserData = (userId) => {
        const period = selectedButton === 'monthly' ? 'last_month' : 'all_time';
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/userStats/${userId}?period=${period}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const s = JSON.parse(this.responseText);
                setGeneralData([s.visual_stats * 100, s.olfactory_stats * 100, s.taste_stats * 100, s.variety_stats * 100, s.country_stats * 100, s.region_stats * 100, s.price_stats * 100]);
            }
        };
        xhr.send();
    };

    const fetchFirstWinegangData = (winegangId) => {
        const period = selectedButton === 'monthly' ? 'last_month' : 'all_time';
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/winegangStats/${winegangId}?period=${period}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const s = JSON.parse(this.responseText);
                setPersonalData([s.visual_stats * 100, s.olfactory_stats * 100, s.taste_stats * 100, s.variety_stats * 100, s.country_stats * 100, s.region_stats * 100, s.price_stats * 100]);
            }
        };
        xhr.send();
    };

    const fetchSecondWinegangData = (winegangId) => {
        const period = selectedButton === 'monthly' ? 'last_month' : 'all_time';
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/winegangStats/${winegangId}?period=${period}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const s = JSON.parse(this.responseText);
                setGeneralData([s.visual_stats * 100, s.olfactory_stats * 100, s.taste_stats * 100, s.variety_stats * 100, s.country_stats * 100, s.region_stats * 100, s.price_stats * 100]);
            }
        };
        xhr.send();
    };

    const fetchWinesOrderData = (userId) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/wines-order/${userId}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const data = JSON.parse(this.responseText);
                setMyWinesOrder(data.last_order);
                setGeneralWinesOrder(data.preferences);
            }
        };
        xhr.send();
    };

    const fetchGeneralData = () => {
        const period = selectedButton === 'monthly' ? 'last_month' : 'all_time';
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/generalStats?period=${period}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const s = JSON.parse(this.responseText);
                setGeneralData([s.visual_stats * 100, s.olfactory_stats * 100, s.taste_stats * 100, s.variety_stats * 100, s.country_stats * 100, s.region_stats * 100, s.price_stats * 100]);
            }
        };
        xhr.send();
    };

    const handleSwipeStart = (e) => {
        swipeStartX.current = e.touches[0].clientX;
    };

    const handleSwipeEnd = (e) => {
        if (swipeStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - swipeStartX.current;
        swipeStartX.current = null;
        if (Math.abs(delta) < 48) return;
        setSelectedButton(prev => prev === 'monthly' ? 'general' : 'monthly');
    };

    const handleFirstChange = (opt) => {
        setFirstValue(opt.value);
        setPersonalLabel(opt.label);
        if (opt.value === 'user') {
            fetchPersonalData(profileInfo.id);
        } else {
            fetchFirstWinegangData(opt.value);
        }
    };

    const handleSecondChange = (opt) => {
        setSecondValue(opt.value);
        setGeneralLabel(opt.label);
        if (opt.value === 'general') {
            fetchGeneralData();
        } else if (opt.value === 'user') {
            fetchSecondUserData(userData.id);
        } else {
            fetchSecondWinegangData(opt.value);
        }
    };

    const isOwnProfile = Number(profileInfo.id) === Number(id);
    const firstDropdownSource = isOwnProfile ? userData : myUserData;

    const firstOptions = [
        { value: 'user', label: firstDropdownSource.username || '…' },
        ...(firstDropdownSource.winegangs?.map(wg => ({ value: String(wg.id), label: wg.winegang_name })) || []),
    ];

    const secondOptions = isOwnProfile
        ? [
            { value: 'general', label: t('stats.participants_level') },
            ...(userData.winegangs?.map(wg => ({ value: String(wg.id), label: wg.winegang_name })) || []),
        ]
        : [
            { value: 'user', label: userData.username || '…' },
            ...(userData.winegangs?.map(wg => ({ value: String(wg.id), label: wg.winegang_name })) || []),
        ];

    const chartData = {
        labels: [
            t('stats.visual_phase'), t('stats.olfactory_phase'), t('stats.taste_phase'),
            t('stats.variety'), t('stats.country'), t('stats.region'), t('stats.price'),
        ],
        datasets: [
            {
                label: personalLabel,
                data: personalData,
                backgroundColor: 'rgba(201, 169, 110, 0.12)',
                borderColor: 'rgba(201, 169, 110, 0.85)',
                borderWidth: 2,
                pointBackgroundColor: 'rgba(201, 169, 110, 1)',
                pointRadius: 3,
                pointHoverRadius: 5,
            },
            {
                label: generalLabel,
                data: generalData,
                backgroundColor: 'rgba(180, 60, 90, 0.12)',
                borderColor: 'rgba(210, 80, 110, 0.85)',
                borderWidth: 2,
                pointBackgroundColor: 'rgba(210, 80, 110, 1)',
                pointRadius: 3,
                pointHoverRadius: 5,
                tension: 0,
            },
        ],
    };

    const isLight = theme === 'light';
    const chartOptions = {
        scales: {
            r: {
                angleLines: { color: isLight ? 'rgba(130, 86, 4, 0.22)' : 'rgba(232, 220, 196, 0.18)' },
                grid: { color: isLight ? 'rgba(130, 86, 4, 0.14)' : 'rgba(232, 220, 196, 0.1)' },
                suggestedMin: 0,
                suggestedMax: 100,
                ticks: { display: false },
                pointLabels: { display: false, font: { size: 0 } },
            },
        },
        plugins: {
            legend: false,
            tooltip: {
                backgroundColor: isLight ? 'rgba(238, 220, 178, 0.98)' : 'rgba(20, 5, 15, 0.92)',
                titleColor: isLight ? '#1a0e00' : '#e8dcc4',
                bodyColor: isLight ? '#5a3800' : '#c9a96e',
                borderColor: isLight ? 'rgba(130, 86, 4, 0.28)' : 'rgba(232, 220, 196, 0.18)',
                borderWidth: 1,
                padding: 10,
                callbacks: {
                    label: (ctx) => {
                        const label = ctx.dataset.label || '';
                        const value = ctx.raw !== undefined ? ctx.raw.toFixed(1) : '0.0';
                        return `${label}: ${value}%`;
                    },
                },
            },
        },
        animation: { duration: 500 },
    };

    return (
        <div className={`st-stage${ready ? ' st-ready' : ''}`}>
            <div className="st-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="st-vignette" />
            <div className="st-grain" />

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>{t('common.retry')}</button>
                </div>
            )}

            {/* Topbar */}
            <div className="st-topbar">
                <button className="st-icon-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <StIcon name="back" />
                </button>
                <div className="st-topbar-title">{t('common.stats')}</div>
                <div className="st-topbar-spacer" />
            </div>

            {/* Profile row */}
            <div className="st-profile-row">
                <div className="st-avatar-wrap">
                    <img src={PROFILE_IMAGES[userData.profile] || PROFILE_IMAGES[0]} alt="" className="st-avatar" />
                </div>
                <div className="st-profile-info">
                    <div className="st-profile-name">{userData.username}</div>
                    <div className="st-profile-meta">
                        <div className="st-stat">
                            <span className="st-stat-value">{parseFloat(userData.average || 0).toFixed(1)}%</span>
                            <span className="st-stat-label">{t('common.accuracy')}</span>
                        </div>
                        <div className="st-stat-divider" />
                        <div className="st-stat">
                            <span className="st-stat-value">{userData.wines || 0}</span>
                            <span className="st-stat-label">{t('common.wines')}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable body */}
            <div className="st-body">

                {/* Period toggle */}
                <div className="st-period-row">
                    <button
                        className={`st-period-btn${selectedButton === 'monthly' ? ' st-active' : ''}`}
                        onClick={() => setSelectedButton('monthly')}
                    >
                        {t('common.monthly')}
                    </button>
                    <button
                        className={`st-period-btn${selectedButton === 'general' ? ' st-active' : ''}`}
                        onClick={() => setSelectedButton('general')}
                    >
                        {t('stats.level_button')}
                    </button>
                </div>

                {/* Radar chart panel — swipe izq/der para cambiar período */}
                <div
                    className="st-chart-panel"
                    onTouchStart={handleSwipeStart}
                    onTouchEnd={handleSwipeEnd}
                >
                    <div className="st-chart-wrap">
                        <Radar data={chartData} options={chartOptions} />
                        <div className="st-ci st-ci-visual"><StIcon name="eye" size={22} /><span className="st-ci-tip">{t('stats.visual_phase')}</span></div>
                        <div className="st-ci st-ci-olfativa"><StIcon name="aroma" size={22} /><span className="st-ci-tip">{t('stats.olfactory_phase')}</span></div>
                        <div className="st-ci st-ci-gusto"><StIcon name="drop" size={22} /><span className="st-ci-tip">{t('stats.taste_phase')}</span></div>
                        <div className="st-ci st-ci-variedad"><StIcon name="grape" size={22} /><span className="st-ci-tip">{t('stats.variety')}</span></div>
                        <div className="st-ci st-ci-pais"><StIcon name="globe" size={22} /><span className="st-ci-tip">{t('stats.country')}</span></div>
                        <div className="st-ci st-ci-region"><StIcon name="pin" size={22} /><span className="st-ci-tip">{t('stats.region')}</span></div>
                        <div className="st-ci st-ci-precio"><StIcon name="tag" size={22} /><span className="st-ci-tip">{t('stats.price')}</span></div>
                    </div>

                    {/* Custom dropdowns */}
                    <div className="st-selectors">
                        <StDropdown
                            options={firstOptions}
                            value={firstValue}
                            onChange={handleFirstChange}
                            accentColor="rgba(201,169,110,0.9)"
                        />
                        <StDropdown
                            options={secondOptions}
                            value={secondValue}
                            onChange={handleSecondChange}
                            accentColor="rgba(210,80,110,0.9)"
                        />
                    </div>
                </div>

                {/* Wines section */}
                <div className="st-wines-section">
                    <div className="st-wines-heading">{t('stats.wine_of_month')}</div>
                    <div className="st-wines-cols">
                        <div className="st-wines-col">
                            <div className="st-wines-label">{t('stats.personal')}</div>
                            <div className="st-wines-row">
                                {myWinesOrder && ['first', 'second', 'third', 'fourth'].map((pos, i) =>
                                    myWinesOrder[pos] ? (
                                        <div className="st-wine-item" key={i} style={{ animationDelay: `${i * 70}ms` }}>
                                            <img src={getWineImage(myWinesOrder[pos])} alt={myWinesOrder[pos]} className="st-wine-img" />
                                            <span className="st-wine-name">{myWinesOrder[pos]}</span>
                                        </div>
                                    ) : null
                                )}
                            </div>
                        </div>
                        <div className="st-wines-col">
                            <div className="st-wines-label">{t('stats.general')}</div>
                            <div className="st-wines-row">
                                {generalWinesOrder.map((order, i) => (
                                    <div className="st-wine-item" key={i} style={{ animationDelay: `${i * 70}ms` }}>
                                        <img src={getWineImage(order.name)} alt={order.name} className="st-wine-img" />
                                        <span className="st-wine-name">{order.name}</span>
                                        <span className="st-wine-pct">{order.percent}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
